/** @jsxImportSource ./simboluri-jsx */
// Structure data provider — the data half of the old structure App.tsx
// ProjectShell, ported onto the unified platform shell (wave 2).
//
// One provider per project, mounted by the platform Shell for every /p/:pid
// route. It owns:
//
//   * the step polling (1,5 s while running / 6 s idle summaries) + result
//     hydration ON THE SCREEN'S PRIORITIES — what the screen draws first,
//     the rest in the background, two at a time (see `planulEcranului`) — with
//     the reference-preserving merge that keeps the 3D scenes from being
//     rebuilt twice a second (see the long comment on mergeSteps), and a
//     per-solution memory so a solution seen once is not downloaded again;
//   * the chain report polling (3 s), on the viewed solution's space;
//   * CE RÂND SE CITEȘTE (contractul «soluțiile pe faze», §8): `:sol` din cale
//     — un singur id, al rândului privit (S0, M sau D) — și `?corp=`. Spațiul
//     de citit e `structura.corpuri[].spatiu` al corpului, altfel id-ul
//     rândului; `?corp=toate` («Toate corpurile») citește spațiul soluției,
//     iar Studioul arată întregul complex. Rândul vine din registrul
//     platformei (`nav/Registru`), nu dintr-un registru al structurii: acela
//     a dispărut (Î23).
//
// Pornirile pe fază NU mai stau aici: «Reia schița», «Rerulează» și «Pornește
// calculul detaliat» șterg ceva înainte (contract §5.1), deci trec toate prin
// `nav/actiuni`, care arată întâi cascada (A1). Un buton de pornire care ocolea
// confirmarea («Lanțul de calcul») ștergea dintr-un clic toate soluțiile unei
// arhitecturi. Aici a rămas doar ■ pe rularea rândului privit.
//
// Fără rând în cale nu se citește NIMIC: spațiul «proiect» nu mai are pași
// (contract §6). Nu există pin și nici ★ la structură; ce se deschide fără id
// decide serverul (`registru.implicit`). Un id necunoscut, al altei
// discipline, sau un corp care nu e al soluției se SPUN (`eroare`), nu se
// aruncă în tăcere (Î24).

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useLocation } from "react-router-dom";
import { stopJob } from "../api";
import {
  getLant,
  getStep,
  listStepsRezumat,
} from "./apiStructura";
import type {
  ApiError,
  BreakpointPas4,
  RaportLant,
  StepRezumat,
  StepState,
} from "./tipuri";
import { lantInLucru } from "./tipuri";
import { useTexteRezultat } from "./texteRezultat";
import { useRegistruOptional } from "../nav/Registru";
import type { CorpRand, SolutieRand } from "../nav/api";
import { CORP_TOATE, citesteRuta, corpulActiv } from "../nav/selectie";
import { refLegatura } from "./legaturi";

// --- the reference-preserving step merge (ported verbatim from App.tsx) -----
// Re-fetching produces NEW objects even when nothing changed on the
// server. A new identity for `state.result` tears down the 3D scenes (camera
// jumps, layer toggles reset, selection lost). A step is "unchanged" when it
// has the same status, the same `updated_at` and the same progress — then the
// old reference is kept so React sees no change. Summaries do not carry
// results; hydration below refreshes them once `updated_at` moves.
function mergeSteps(
  old: Record<string, StepState>,
  fresh: Record<string, StepRezumat>
): Record<string, StepState> {
  const out: Record<string, StepState> = {};
  for (const k of Object.keys(fresh)) {
    const v = old[k];
    const n = fresh[k];
    if (
      v &&
      v.status === n.status &&
      v.updated_at === n.updated_at &&
      v.fundatie_adoptata === n.fundatie_adoptata &&
      JSON.stringify(v.progres ?? null) === JSON.stringify(n.progres ?? null)
    ) {
      out[k] = v;
      continue;
    }
    // A step whose server result we have not fetched yet must NOT enter the
    // state: components read `state.result.<x>` as soon as they see a step,
    // and a null result crashes them. A missing key is a state they know.
    if (n.are_rezultat && !v?.result) continue;
    out[k] = { ...n, result: v?.result ?? null };
  }
  const keys = Object.keys(out);
  const identical =
    keys.length === Object.keys(old).length && keys.every((k) => out[k] === old[k]);
  return identical ? old : out;
}

function aceleasiRezumate(
  a: Record<string, StepRezumat>,
  b: Record<string, StepRezumat>
): boolean {
  const ka = Object.keys(a);
  if (ka.length !== Object.keys(b).length) return false;
  return ka.every((k) => {
    const x = a[k];
    const y = b[k];
    return !!y && x.status === y.status && x.updated_at === y.updated_at
      && x.are_rezultat === y.are_rezultat
      && JSON.stringify(x.progres ?? null) === JSON.stringify(y.progres ?? null);
  });
}

// A step needs its result fetched when the server has one and ours is missing
// or older than the server's `updated_at`.
function needsResult(v: StepState | undefined, n: StepRezumat): boolean {
  if (!n.are_rezultat) return false;
  return !v || v.result === null || v.updated_at !== n.updated_at;
}

// --- ce pași cere fiecare ecran, și în ce ordine ----------------------------
//
// Rezultatele pașilor au megaocteți (model_fem, încărcări, armări), iar un
// ecran folosește unul sau patru din cele douăzeci. Se cereau TOATE, deodată,
// pe orice ecran care desenează ceva: pe Studio, modelul 3D aștepta după
// nouăsprezece răspunsuri de care nu avea nevoie, într-o coadă de patru
// workeri. Aici fiecare ecran spune ce îi trebuie ÎNTÂI — se cere imediat, în
// paralel — și tot restul se aduce ÎN FUNDAL, câte două, abia după ce au sosit
// primele: așa clicul pe «2D», «Moduri» sau pe fișa soluției găsește datele
// deja acasă, fără ca vreunul să-i fi stat în cale modelului 3D.
//
// Datele aduse RĂMÂN în furnizor cât timp se stă pe aceeași soluție, oricâte
// ecrane s-ar schimba; sondajul de rezumate le reîmprospătează numai când
// `updated_at` s-a mutat pe server (v. `needsResult`).

export type PlanIncarcare = {
  /** pașii ecranului, în ordinea în care îi cere; se aduc imediat */
  intai: string[];
  /** pașii de adus în fundal după ei, înaintea celorlalți */
  apoi: string[];
};

/** Pașii Studioului, în ordinea taburilor lui: modelul 3D întâi și singur;
 *  apoi încărcările (tabul «Încărcări»), planul (2D: Pasul 4 și fundația) și,
 *  la urmă, Pasul 19 — de acolo vin CLASELE elementelor («Stâlpul ST15.5
 *  (tip SA)» în capul panoului, rândul «Clasă»); fără el titlul rămânea fără
 *  tip pe orice piesă aleasă în Studio. */
const STUDIO: PlanIncarcare = {
  intai: ["model_fem"],
  apoi: ["incarcari", "model_structura", "verificare_fundatie",
         "rezistenta_fundatie", "optimizare_structura"],
};
const ARMARI = ["armare_grinzi", "armare_stalpi", "armare_pereti", "armare_placa",
                "armare_fundatie"];

/** Verificările: tabloul care adună TOATE verdictele structurii nu cere
 *  niciun rezultat de pas. Îl compune serverul din cei 13 pași care emit
 *  verdicte (`structure/tablou_verificari.py`), iar ecranul primește doar
 *  sumarul și, la deschiderea unui card, rândurile lui (`getVerificariSumar`,
 *  `getVerificariRanduri`). Pagina unui TIP de verificare citește numai
 *  picatele Pasului 19. */
const VERIFICARI: PlanIncarcare = { intai: [], apoi: [] };
const VERIFICARE_TIP: PlanIncarcare = { intai: ["optimizare_structura"], apoi: [] };

/** Ce cere ecranul de la `pathname`. `null` = niciun rezultat (Soluții, Dosar,
 *  Localizare, Setări): acolo rămân doar rezumatele.
 *
 *  `cautare` e `location.search`, și e nevoie de el într-un singur loc: vederea
 *  structurii (`?vedere=`). «Model 3D», «Moduri» și «Încărcări» desenează
 *  modelul FEM; «Rezumat», «Lanțul de calcul» și «Setări de calcul» citesc
 *  registrul și rezumatele pașilor, iar «Componente» își cere singură devizul
 *  (`getEstimare`), deci niciuna nu cere megaocteți. Fără `vedere`, planul e
 *  al modelului — vederea implicită a unei soluții încheiate. */
export function planulEcranului(pathname: string,
                                cautare = ""): PlanIncarcare | null {
  const ruta = citesteRuta(pathname, cautare);
  if (!ruta.projectId) return null;
  switch (ruta.pagina) {
    case "situatie":
    case "arhitectura":
    case "costuri":
      return { intai: [], apoi: [] };
    case "verificari":
      return ruta.sub?.fel === "tip" ? VERIFICARE_TIP : VERIFICARI;
    case "structura": {
      if (ruta.sub?.fel === "element") {
        // axele de fundare se construiesc din Pașii 17 și 6
        // (`fundatiaAdoptata`), iar stâlpul își desenează fundația de sub el
        // din aceiași pași: fără ei, pagina deschisă direct pe un ax ieșea
        // «negăsită»
        return { intai: [...ARMARI, "model_fem", "optimizare_structura",
                         "verificare_fundatie", "rezistenta_fundatie"],
                 apoi: [] };
      }
      if (ruta.sub?.fel === "pas") {
        const pas = ruta.sub.sectiune;
        if (pas === "elemente") {
          return { intai: [...ARMARI, "model_fem", "optimizare_structura"], apoi: [] };
        }
        if (pas === "fisa") return { intai: ["optimizare_structura"], apoi: [] };
        if (pas === "acasa") return { intai: ["model_fem", "optimizare_structura"], apoi: [] };
        if (pas === "imbunatatiri") return { intai: [], apoi: [] };
        // pagina pasului cere pasul ei; antetul (PasAntet) citește verdictele
        // Pasului 19, iar Pasul 8 își desenează încărcările pe modelul Pasului 7
        const intai = [pas];
        if (pas === "incarcari") intai.push("model_fem");
        if (pas === "rezistenta_fundatie") intai.push("verificare_fundatie");
        intai.push("optimizare_structura");
        return { intai, apoi: [] };
      }
      if (ruta.vedere === "componente" || ruta.vedere === "lant"
          || ruta.vedere === "setari") {
        return { intai: [], apoi: [] };
      }
      return STUDIO;
    }
  }
  return null;
}

/** Planul ecranului cu pașii ceruți la cerere (`cerePasi`) adăugați la
 *  `intai`. Un ecran fără plan (`null`) nu citește rezultate, deci nici
 *  cererile nu-l schimbă. */
export function cuPasiCeruti(plan: PlanIncarcare | null,
                             ceruti: readonly string[]): PlanIncarcare | null {
  if (!plan || !ceruti.length) return plan;
  const noi = ceruti.filter((k) => !plan.intai.includes(k));
  return noi.length ? { ...plan, intai: [...plan.intai, ...noi] } : plan;
}

/** Ordinea în care se aduc pașii lipsă: cei ceruți întâi, apoi vecinii —
 *  taburile aceluiași ecran. Fără dubluri.
 *
 *  AICI SE OPREȘTE. Fără oprire, fundalul ar continua cu
 *  TOT lanțul, în ordinea pașilor: deschis Studioul, interfața trăgea în urma
 *  modelului 3D și cele nouăsprezece rezultate rămase. Măsurat în bază pe
 *  mediul lui Stefan, alea sunt zeci de megaocteți per soluție — `armare_stalpi`
 *  singur are 3,6 MB pe un corp, `armare_grinzi` 2,7 — pentru ecrane pe care
 *  nimeni nu apucase încă să le ceară. Argumentul de atunci („clicul găsește
 *  datele acasă") rămâne valabil pentru VECINI, adică pentru taburile aceluiași
 *  ecran, și de aia `apoi` se aduce în continuare; pentru restul lanțului
 *  argumentul era o presupunere despre unde se duce omul, plătită de fiecare
 *  dată, inclusiv când nu se ducea nicăieri.
 *
 *  Ce se pierde: prima deschidere a unui ecran depărtat își cere singură pasul,
 *  ca prioritar — adică exact drumul pe care îl are oricum orice ecran
 *  (`planulEcranului`), doar că nu mai e plătit dinainte de toată lumea. */
export function ordineaIncarcarii(plan: PlanIncarcare, lipsa: readonly string[]):
    { imediat: string[]; fundal: string[] } {
  const e = new Set(lipsa);
  const imediat = plan.intai.filter((k) => e.has(k));
  const vazut = new Set(imediat);
  const fundal: string[] = [];
  for (const k of plan.apoi) {
    if (e.has(k) && !vazut.has(k)) { vazut.add(k); fundal.push(k); }
  }
  return { imediat, fundal };
}

/** Câte rezultate se aduc deodată în fundal. Două: destul ca restul să sosească
 *  în câteva secunde, puțin cât să nu ocupe workerii serviciului. */
const FUNDAL_SIMULTAN = 2;

/** De ce nu se poate citi rândul cerut: id-ul nu e în registru, e al altei
 *  discipline, sau corpul din `?corp=` nu e al soluției. */
export type EroareStructura = "necunoscuta" | "alta_disciplina" | "corp_necunoscut";

export type StructureData = {
  projectId: string;
  /** REFERINȚA DE LEGĂTURĂ a rândului privit — id-ul, plus `~corp` când s-a
   *  ales explicit (v. `legaturi.refLegatura`). Paginile o dau înapoi
   *  constructorilor de linkuri. `null` = niciun rând de structură în cale. */
  solutie: string | null;
  /** spațiul din care se citesc pașii: al corpului activ, altfel al rândului */
  cloneId: string | null;
  /** rândul de structură privit (S0, M sau D) */
  solutiaCurenta: SolutieRand | null;
  /** corpurile rândului (gol la o soluție cu o singură clădire), cu `activ`
   *  pe cel din al cărui spațiu se citesc pașii acum */
  corpuri: (CorpRand & { activ: boolean })[];
  /** «Toate corpurile» (`?corp=toate`): niciun corp activ, se citește
   *  spațiul soluției, iar Studioul arată întregul complex */
  toateCorpurile: boolean;
  /** eroarea rutei, arătată de ecran în locul datelor */
  eroare: EroareStructura | null;
  steps: Record<string, StepState>;
  /** the server's short summaries, as last polled; `null` until the first
   *  answer. A step with `are_rezultat` that is missing from `steps` is one
   *  whose result is still on its way — the only way a screen can tell
   *  "loading" from "there is no such result" (see `schelet.stareModel`) */
  rezumate: Record<string, StepRezumat> | null;
  /** state for a step page: hides progress-only rows without result */
  statePas: (k: string) => StepState | null;
  /** true while the server works on a step whose result is not there yet */
  ruleazaPagina: (k: string) => boolean;
  /** the step the server grinds INSIDE a long step (Pasul 19 reruns 4→14) */
  /** rândurile de structură din registru */
  solutii: SolutieRand[];
  /** stările înghețate ale Pasului 4 (`result.breakpoints`), cerute cu `?bp=` */
  breakpoints: BreakpointPas4[];
  lant: RaportLant | null;
  lantRuleaza: boolean;
  /** de ce n-a mers ■ */
  runError: string | null;
  /** ■ pe rularea deschisă a rândului privit */
  opreste: () => Promise<void>;
  refreshSteps: () => void;
  /** Cere pașii dați ÎNTÂI, peste planul ecranului, cât se stă pe spațiul
   *  ăsta. Pentru ce nu se știe dinainte: drawer-ul Studioului are nevoie de
   *  armarea familiei piesei alese (verdictul, costul), dar numai de ea —
   *  planul Studioului nu aduce armările (megaocteți, v. `ordineaIncarcarii`). */
  cerePasi: (pasi: readonly string[]) => void;
  /** cere registrul din nou; promisiunea se ține când a sosit */
  refreshSolutii: () => Promise<void>;
  /** Uită rezultatele ținute pentru spațiile prin care s-a trecut. O cheamă
   *  pagina soluțiilor — v. `uitaCitite` mai jos. */
  uitaCitite: () => void;
  /** versiunea dicționarului cu textele rezultatelor (v. `texteRezultat`):
   *  crește când sosește, ca ecranele să se redeseneze traduse */
  texteRezultat: number;
};

const Ctx = createContext<StructureData | null>(null);

export function useStructure(): StructureData {
  const v = useContext(Ctx);
  if (!v) throw new Error("useStructure outside <StructureProvider>");
  return v;
}

export function StructureProvider({
  projectId,
  children,
}: {
  projectId: string;
  children: ReactNode;
}) {
  const reg = useRegistruOptional();
  const registru = reg?.registru ?? null;
  // Rezultatele COMPLETE ale pașilor (megabytes: model_fem, încărcări…) se
  // aduc numai pe ecranele care le desenează — pașii, Studio, verificările,
  // costurile, fișa soluției. Furnizorul e montat pe TOATE ecranele
  // proiectului, iar pe Soluții, Dosar, Localizare, Setări ele nu
  // interesează pe nimeni; acolo rămân doar rezumatele.
  const { pathname, search } = useLocation();
  // pașii ceruți LA CERERE de un ecran, peste planul lui (v. `cerePasi`)
  const [ceruti, setCeruti] = useState<readonly string[]>([]);
  const plan = useMemo(
    () => cuPasiCeruti(planulEcranului(pathname, search), ceruti),
    [pathname, search, ceruti]);
  const ruta = useMemo(() => citesteRuta(pathname, search), [pathname, search]);

  const [steps, setSteps] = useState<Record<string, StepState>>({});
  const [rezumate, setRezumate] = useState<Record<string, StepRezumat> | null>(null);
  const [lant, setLant] = useState<RaportLant | null>(null);
  const [runError, setRunError] = useState<string | null>(null);

  const lantRuleaza = lantInLucru(lant);
  const texteRezultat = useTexteRezultat();

  // RÂNDUL din cale, dacă e al structurii. Pe `/structura/:sol` o cale spre
  // alt fel de rând e o eroare; pe `/verificari/:sol` și `/costuri/:sol` un
  // rând de arhitectură e legitim — atunci structura pur și simplu nu citește.
  const solutii = useMemo(
    () => (registru?.solutii ?? []).filter((s) => s.discipline === "structure"),
    [registru]);
  const randCerut = registru && ruta.sol
    ? registru.solutii.find((s) => s.id === ruta.sol) ?? null : null;
  const solutiaCurenta = randCerut?.discipline === "structure" ? randCerut : null;
  const corpuriRand = useMemo(() => solutiaCurenta?.structura?.corpuri ?? [],
                              [solutiaCurenta]);
  const corpActiv = useMemo(() => corpulActiv(corpuriRand, ruta.corp),
                            [corpuriRand, ruta.corp]);
  let eroare: EroareStructura | null = null;
  if (registru && ruta.sol && (ruta.pagina === "structura" || ruta.pagina === "costuri")) {
    // un id pe care registrul ținut nu-l are e «necunoscut» abia după ce a
    // fost căutat din nou (v. `nav/Registru`, `useFir`): rândul abia creat
    // nu e încă în el
    if (!randCerut) eroare = !reg || reg.negasit(ruta.sol) ? "necunoscuta" : null;
    else if (randCerut.discipline !== "structure") eroare = "alta_disciplina";
  }
  if (!eroare && solutiaCurenta && ruta.corp && ruta.corp !== CORP_TOATE
      && corpuriRand.length && !corpActiv) {
    eroare = "corp_necunoscut";
  }

  // Spaces the server answered 404 for. A space can disappear under us (a new
  // run wipes it), or not exist yet (a row still queued), and asking for it
  // again every second is how one browser tab saturated the platform — so a
  // missing space is remembered and not read again, until the row itself
  // changes state (it started, it was rerun).
  const [cloneLipsa, setCloneLipsa] = useState<ReadonlySet<string>>(
    () => new Set()
  );
  useEffect(() => setCloneLipsa(new Set()),
            [projectId, solutiaCurenta?.status, solutiaCurenta?.rulare?.status]);

  // STAREA ÎNGHEȚATĂ a Pasului 4 cerută prin adresă (`?bp=3`), numai pe pagina
  // Pasului 4: e o parte a rezultatului pasului, cerută de la pasul însuși.
  const subpas = ruta.sub?.fel === "pas" && ruta.sub.sectiune === "model_structura"
    ? new URLSearchParams(search).get("bp") : null;

  // Spațiul de lucru al rândului privit: al corpului activ, altfel al rândului.
  const cloneId = useMemo(() => {
    if (!solutiaCurenta || eroare) return null;
    return corpActiv?.spatiu ?? solutiaCurenta.id;
  }, [solutiaCurenta, corpActiv, eroare]);
  const spatiuLipsa = cloneId != null && cloneLipsa.has(cloneId);

  // «toate» nu e un corp: linkurile paginilor nu-l poartă (fiecare pagină
  // de pas sau de element e a unui corp)
  const solutie = refLegatura(solutiaCurenta?.id,
                              ruta.corp === CORP_TOATE ? null : ruta.corp);
  const toateCorpurile = ruta.corp === CORP_TOATE && corpuriRand.length > 1;

  // What we already have, readable inside a .then without re-arming the
  // interval; plus the set of steps whose result is being fetched right now.
  const stepsRef = useRef<Record<string, StepState>>({});
  useEffect(() => {
    stepsRef.current = steps;
  }, [steps]);
  const fetching = useRef<Set<string>>(new Set());

  /** Rezultatele fiecărui spațiu de citit prin care s-a trecut, ca întoarcerea
   *  la o soluție văzută deja să nu le mai descarce o dată. Cheia e spațiul
   *  (proiect + spațiu + subpas); rezumatele următoare aruncă ce s-a schimbat
   *  între timp pe server, prin `needsResult`. */
  const memorie = useRef(new Map<string, Record<string, StepState>>());
  const spatiuCurent = useRef<string | null>(null);
  /** Coada de fundal și ce e în zbor din ea; prioritarii se numără separat,
   *  fiindcă fundalul pornește abia când ei au sosit. */
  const coada = useRef<string[]>([]);
  const inZborFundal = useRef(0);
  const prioritari = useRef<Set<string>>(new Set());

  // La schimbarea spațiului, ce s-a adus până acum e al altui spațiu: se
  // pornește din nou, din ce s-a mai văzut pentru spațiul ăsta, dacă s-a văzut.
  useEffect(() => {
    const cheie = `${projectId}|${cloneId ?? ""}|${subpas ?? ""}`;
    if (spatiuCurent.current && spatiuCurent.current !== cheie) {
      memorie.current.set(spatiuCurent.current, stepsRef.current);
      // cererile erau ale spațiului părăsit; cine are nevoie cere din nou
      setCeruti([]);
    }
    spatiuCurent.current = cheie;
    const stiute = memorie.current.get(cheie) ?? {};
    setSteps(stiute);
    setRezumate(null);
    stepsRef.current = stiute;
    fetching.current = new Set();
    coada.current = [];
    inZborFundal.current = 0;
    prioritari.current = new Set();
  }, [projectId, cloneId, subpas]);

  const refreshSteps = useCallback(() => {
    // fără rând nu se citește nimic: spațiul «proiect» n-are pași (contract §6)
    if (cloneId == null || spatiuLipsa) return;
    listStepsRezumat(projectId, cloneId)
      .then((fresh) => {
        setSteps((old) => mergeSteps(old, fresh));
        // same identity when nothing changed: a new object every poll would
        // re-render every consumer (the 3D views included) for nothing
        setRezumate((old) => (old && aceleasiRezumate(old, fresh) ? old : fresh));
        if (!plan) return;
        const lipsa = Object.entries(fresh)
          .filter(([k, n]) => !fetching.current.has(k)
                              && needsResult(stepsRef.current[k], n))
          .map(([k]) => k);
        const { imediat, fundal } = ordineaIncarcarii(plan, lipsa);
        // Un pas cerut ÎNTÂI de ecranul pe care tocmai s-a ajuns nu mai
        // așteaptă la rândul lui din fundal.
        coada.current = [...coada.current.filter((k) => !imediat.includes(k)),
                         ...fundal.filter((k) => !coada.current.includes(k))];
        const cere = (k: string, prioritar: boolean) => {
          fetching.current.add(k);
          if (prioritar) prioritari.current.add(k);
          else inZborFundal.current += 1;
          // numai Pasul 4 are stare înghețată; ceilalți pași ai unui subpas
          // sunt cei ai rândului (subpasul nu-i schimbă)
          getStep(projectId, k, cloneId,
                  k === "model_structura" ? subpas : null)
            .then((full) =>
              // the progress belongs to the latest summary, not to the moment
              // this request left
              setSteps((v) => ({ ...v, [k]: { ...full, progres: v[k]?.progres } }))
            )
            .catch(() => {})
            .finally(() => {
              fetching.current.delete(k);
              if (prioritar) prioritari.current.delete(k);
              else inZborFundal.current -= 1;
              pornesteFundal();
            });
        };
        // Fundalul curge câte două, și numai când nu mai e nimic prioritar pe
        // drum: modelul 3D nu împarte banda și workerii cu nimeni.
        const pornesteFundal = () => {
          while (prioritari.current.size === 0
                 && inZborFundal.current < FUNDAL_SIMULTAN
                 && coada.current.length) {
            const k = coada.current.shift()!;
            if (fetching.current.has(k)) continue;
            cere(k, false);
          }
        };
        imediat.forEach((k) => cere(k, true));
        pornesteFundal();
      })
      .catch((e: ApiError) => {
        // Spațiul rândului a dispărut (o rerulare, o ștergere): nu se mai
        // cere. Registrul, la următorul sondaj, spune ce s-a întâmplat cu
        // rândul — ecranul arată atunci eroarea, nu un spațiu gol.
        if (e?.status === 404 && cloneId != null) {
          setCloneLipsa((v) => new Set(v).add(cloneId));
          // «nu există», nu «se încarcă»: ecranul spune că lipsește modelul
          setRezumate((v) => v ?? {});
        }
      });
  }, [projectId, cloneId, subpas, plan, spatiuLipsa]);

  useEffect(refreshSteps, [refreshSteps]);

  // 1,5 s cât rulează ceva, 6 s când nu — nu 0,5 s.
  //
  // Jumătatea de secundă era potrivită când pagina vorbea direct cu Flask-ul
  // structurii, pe localhost. Azi fiecare tic e o cerere prin control-plane
  // către un serviciu care poate CALCULA, iar o filă deschisă însemna 2 cereri
  // pe secundă de proxy peste un solver ocupat. Starea de pe ecran rămâne a
  // serverului: la revenirea în filă se cere imediat, iar pașii lungi oricum
  // nu se termină în jumătate de secundă.
  const randViu = !!solutiaCurenta?.rulare;
  useEffect(() => {
    if (cloneId == null || spatiuLipsa) return;
    const interval = lantRuleaza || randViu ? 1500 : 6000;
    const t = setInterval(() => {
      if (!document.hidden) refreshSteps();
    }, interval);
    const now = () => {
      if (!document.hidden) refreshSteps();
    };
    document.addEventListener("visibilitychange", now);
    window.addEventListener("focus", now);
    return () => {
      clearInterval(t);
      document.removeEventListener("visibilitychange", now);
      window.removeEventListener("focus", now);
    };
  }, [refreshSteps, lantRuleaza, randViu, cloneId, spatiuLipsa]);

  // Raportul lanțului, pe spațiul rândului privit (contract §6: `…/lant` stă
  // pe spațiul soluției; pe proiect au rămas doar setările).
  useEffect(() => {
    setLant(null);
    if (cloneId == null || spatiuLipsa) return;
    let alive = true;
    const take = () => {
      if (document.hidden) return;
      getLant(projectId, cloneId)
        .then((r) => alive && setLant(r))
        .catch(() => {});
    };
    take();
    // Ca la pași: des cât rulează, rar când nu. Raportul lanțului trece și el
    // prin control-plane spre serviciul care calculează.
    const t = setInterval(take, randViu ? 3000 : 8000);
    return () => {
      alive = false;
      clearInterval(t);
    };
  }, [projectId, cloneId, randViu, spatiuLipsa]);

  // A step may HAVE a row without a result: the row is created by the
  // progress panel while the server works, and the previous result was
  // deleted. Components read `state.result.<x>` directly, so such a row is
  // presented as "not run"; the page shows the spinner if the server works.
  const statePas = useCallback(
    (k: string): StepState | null => {
      const s = steps[k];
      if (!s) return null;
      if (s.status !== "error" && !s.result && !s.progres) return null;
      return s;
    },
    [steps]
  );
  const ruleazaPagina = useCallback(
    (k: string) => !!steps[k]?.progres && !steps[k]?.result,
    [steps]
  );

  const breakpoints = useMemo(
    () => ((steps["model_structura"]?.result as { breakpoints?: BreakpointPas4[] } | null)
      ?.breakpoints ?? []),
    [steps]);

  const corpuri = useMemo(
    () => corpuriRand.map((c) => ({ ...c, activ: c.cod === corpActiv?.cod })),
    [corpuriRand, corpActiv]);

  const cerePasi = useCallback((pasi: readonly string[]) => {
    setCeruti((v) => {
      const noi = pasi.filter((k) => !v.includes(k));
      return noi.length ? [...v, ...noi] : v;
    });
  }, []);

  const refreshSolutii = useCallback(async () => {
    await reg?.reincarca();
  }, [reg]);

  const opreste = useCallback(async () => {
    const job = solutiaCurenta?.rulare?.job_id;
    if (!job) return;
    try {
      await stopJob(projectId, job);
      await reg?.reincarca();
    } catch (e) {
      setRunError((e as Error).message);
    }
  }, [projectId, solutiaCurenta, reg]);

  /** GOLIREA MEMORIEI, din pagina soluțiilor.
   *
   *  Regula e a întregii interfețe, nu doar a structurii:
   *  ce s-a adus rămâne pe client cât timp omul se plimbă între ecranele de
   *  desen, și se uită când ajunge la Soluții — acolo se pornesc rulări și se
   *  șterg soluții, deci acolo e granița după care nu mai răspundem de ce
   *  ținem.
   *
   *  Nu golește ce e PE ECRAN acum (`steps`): pagina soluțiilor nu citește
   *  rezultate (v. `planulEcranului`), iar la următorul ecran de desen sondajul
   *  de rezumate le cere din nou. */
  const uitaCitite = useCallback(() => {
    memorie.current.clear();
    if (spatiuCurent.current) {
      setSteps({});
      stepsRef.current = {};
      fetching.current = new Set();
      coada.current = [];
      inZborFundal.current = 0;
      prioritari.current = new Set();
    }
  }, []);

  const value: StructureData = {
    projectId,
    solutie,
    cloneId,
    solutiaCurenta,
    corpuri,
    toateCorpurile,
    eroare,
    steps,
    rezumate,
    statePas,
    ruleazaPagina,
    solutii,
    breakpoints,
    lant,
    lantRuleaza,
    runError,
    opreste,
    refreshSteps,
    cerePasi,
    refreshSolutii,
    uitaCitite,
    texteRezultat,
  };

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}
